// src/App.jsx
// 🔥 APP.JSX ENTERPRISE V20 — VITRINA PÚBLICA ULTRA RÁPIDA (React.lazy)
// ✅ V20: PublicPropertiesList con React.lazy + Suspense + Skeleton
// ✅ V19: Feature Mensagens V2 ahora en /mensagens/* (reemplaza al viejo)
// ✅ V19: /conversas/* redirige a /mensagens (bookmarks antiguos)
// ✅ V19: /mensagens-legacy disponible como fallback de emergencia 48h
// ✅ V18: Feature Mensagens V2 en /conversas (convive con /mensagens viejo)
// ✅ V17: PaymentProvider global en rutas protegidas (fix UpgradeModal usePayment)
// ✅ V17: PlanGuard activo en todas las páginas privadas
// ✅ V16: Imports lazy simplificados (sin .then redundante)
// ✅ V15: Rutas de pago (success/pending/cancel) registradas
// ✅ V15: Ruta /checkout redirige a /planos (fix UpgradeModal)
// ✅ V15: PaymentProvider en rutas de pago para contexto compartido
// ✅ V14: ThemeProvider unificado (solo en main.jsx)
// ✅ V13: Middleware de seguridad para rutas públicas
// ✅ V13: Headers de seguridad (X-Frame-Options, X-Content-Type-Options)
// ✅ V13: Verificación de carga de DataContext
// ✅ V13: Manejo de errores global mejorado
// ✅ VERSÃO CORRIGIDA - HOOKS SEMPRE NA MESMA ORDEM
// ✅ VERSÃO COM ROTAS PREMIUM + HELP CENTER
// ✅ IMPORTACIONES COMPLETAS · SIN REDUNDANCIAS · PROFESIONAL

import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";

import { AuthProvider } from "./contexts/AuthProvider";
import { useAuth } from "./contexts/useAuth";
import { NotificationsProvider } from "./contexts/NotificationsContext";
// ❌ ELIMINADO: ThemeProvider ya está en main.jsx (evita duplicación)

import { PrivateRoute } from "./components/PrivateRoute";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { PageLoader } from "./components/Loading/PageLoader";
// import { Login } from "./components/Login"; // 🔥 V3.0: Desactivado - usando LoginPremium

// ============================================================
// 🆕 COMPONENTES DE LOGIN PREMIUM
// ============================================================
import LoginPremium from "./components/Login/LoginPremium";
import SignUp from "./components/Login/SignUp";
import ForgotPassword from "./components/Login/ForgotPassword";
import Onboarding from "./components/Login/Onboarding";

// ============================================================
// 📦 PÁGINAS (directos)
// ============================================================
import Calendario from "./pages/Calendario";
import TaskForm from "./pages/TaskForm";
import TaskDetails from "./pages/TaskDetails";
import PropertyForm from "./pages/PropertyForm";
import ClientDetails from "./pages/ClientDetails";
import ClientEdit from "./pages/ClientEdit";
import PropertyDetails from "./pages/PropertyDetails";
import Documentos from "./pages/Documentos";
import Financeiro from "./pages/Financeiro";
import PublicProperty from "./pages/PublicProperty";
// 🔥 V20: PublicPropertiesList ahora es lazy (carga ultra rápida)
// import PublicPropertiesList from "./pages/PublicPropertiesList";
import MigrationDashboard from "./components/admin/MigrationDashboard";
import TestFirebasePage from "./pages/TestFirebasePage";
import AdminMigrationPage from "./pages/AdminMigrationPage";
import Mensagens from "./pages/Mensagens";
import MessageDetails from "./pages/MessageDetails";

// ============================================================
// 🆕 FEATURE MENSAGENS V2 — Sistema WhatsApp-Grade
//    V19: ACTIVADO en /mensagens/* — Reemplaza al sistema viejo
// ============================================================
import { MensagensRoutes } from "./features/mensagens/routes";

// ============================================================
// 💳 PLANOS Y PAGOS
// ============================================================
import PlanosPage from "./pages/Payment/PlanosPage";
import SuccessPage from "./pages/Payment/SuccessPage";
import PendingPage from "./pages/Payment/PendingPage";
import CancelPage from "./pages/Payment/CancelPage";
import { PaymentProvider } from "./contexts/PaymentContext/PaymentContext";

// ============================================================
// 🆕 HELP CENTER PAGES (V12)
// ============================================================
import Documentacao from "./pages/help/Documentacao";
import Tutorial from "./pages/help/Tutorial";
import Suporte from "./pages/help/Suporte";
import Changelog from "./pages/help/Changelog";

// ============================================================
// 📦 DATA CONTEXT E STRATEGIES
// ============================================================
import DataContext from "./lib/data/context/DataContext";
import CloudStrategy from "./lib/data/strategies/CloudStrategy";

// EXPONER GLOBALMENTE PARA PRUEBAS EN CONSOLA
if (typeof window !== "undefined") {
  window.DataContext = DataContext;
  window.CloudStrategy = CloudStrategy;
  console.log("✅ DataContext y CloudStrategy expuestos globalmente para testing");
}

// ============================================================
// 📦 LAZY PAGES (carga diferida) — V20: +PublicPropertiesList
// ============================================================
const Layout = lazy(() => import("./components/Layout"));
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const Clients = lazy(() => import("./pages/Clients.jsx"));
const Pipeline = lazy(() => import("./pages/Pipeline.jsx"));
const Equipe = lazy(() => import("./pages/Equipe.jsx"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite.jsx"));
const Properties = lazy(() => import("./pages/Properties.jsx"));
const Tasks = lazy(() => import("./pages/Tasks.jsx"));
const Reports = lazy(() => import("./pages/Reports.jsx"));
const Settings = lazy(() => import("./pages/Settings.jsx"));
const Profile = lazy(() => import("./pages/Profile.jsx"));
// 🔥 V20: Vitrina pública con lazy loading — carga en ~0ms con skeleton
const PublicPropertiesList = lazy(() => import("./pages/PublicPropertiesList.jsx"));

// 🔥 V20: Skeleton para vitrina pública (se muestra mientras carga el chunk)
const VitrineSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
    <div className="max-w-7xl mx-auto px-4 py-8 animate-pulse space-y-8">
      {/* Hero skeleton */}
      <div className="h-64 bg-slate-200 rounded-2xl" />
      {/* Chips skeleton */}
      <div className="flex gap-2 overflow-hidden">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="h-10 w-24 bg-slate-200 rounded-full flex-shrink-0" />
        ))}
      </div>
      {/* Cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="aspect-[16/10] bg-slate-200 rounded-2xl" />
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// 🎣 CUSTOM HOOK PARA INICIALIZAR DATACONTEXT
// ============================================================
const useInitializeDataContext = (user) => {
  React.useEffect(() => {
    if (!user?.email) return;

    try {
      const imoveisproDb = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const userData = imoveisproDb.users?.find(u => u.email === user.email);
      
      if (userData) {
        DataContext.setCurrentUser(userData.id, userData.email);
        console.log(`👤 DataContext inicializado para: ${userData.email} (ID: ${userData.id})`);
      } else {
        console.warn("⚠️ Usuario no encontrado en imoveispro_db");
      }
    } catch (error) {
      console.error("❌ Erro ao inicializar DataContext:", error);
    }
  }, [user?.email]);
};

// ============================================================
// 🛡️ V13: HOOK DE SEGURIDAD PARA HEADERS
// ============================================================
const useSecurityHeaders = () => {
  useEffect(() => {
    const setMetaTag = (httpEquiv, content) => {
      let meta = document.querySelector(`meta[http-equiv="${httpEquiv}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.httpEquiv = httpEquiv;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    try {
      setMetaTag('X-Frame-Options', 'DENY');
      setMetaTag('X-Content-Type-Options', 'nosniff');
      setMetaTag('Referrer-Policy', 'strict-origin-when-cross-origin');
      
      console.log("🛡️ [V20] Headers de seguridad configurados");
    } catch (error) {
      console.warn("⚠️ [V20] Error configurando headers de seguridad:", error.message);
    }
  }, []);
};

// ✅ Suspense wrapper global
const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// ✅ Layout protegido con PaymentProvider (V17: fix UpgradeModal usePayment)
function ProtectedLayout() {
  return (
    <PaymentProvider>
      <SuspenseWrapper>
        <Layout>
          <Outlet />
        </Layout>
      </SuspenseWrapper>
    </PaymentProvider>
  );
}

// ============================================================
// ✅ WRAPPER PROFESIONAL - HOOKS NA MESMA ORDEM
// ============================================================
function AppWithProviders() {
  const auth = useAuth();
  useInitializeDataContext(auth?.user);
  useSecurityHeaders();

  if (!auth || auth.loadingAuth) {
    return <PageLoader />;
  }

  const { user } = auth;

  return (
    <NotificationsProvider userId={user?.email}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Routes>
          {/* ============================================ */}
          {/* 🌍 ROTAS 100% PÚBLICAS */}
          {/* ============================================ */}
          <Route path="/login" element={<LoginPremium />} />
          {/* <Route path="/premium-login" element={<LoginPremium />} /> */}
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<LoginPremium initialMode="reset-password" />} />
          <Route path="/verify-email" element={<LoginPremium initialMode="verify-email" />} />
          <Route path="/onboarding" element={<Onboarding />} />

          {/* ✉️ Convite de equipe (público — aceitar com login) */}
          <Route
            path="/convite/:token"
            element={
              <Suspense fallback={<PageLoader />}>
                <AcceptInvite />
              </Suspense>
            }
          />
          
          {/* 🔧 RUTAS DE TESTE Y ADMIN */}
          <Route path="/test-firebase" element={<TestFirebasePage />} />
          <Route path="/admin/migration" element={<AdminMigrationPage />} />

          {/* ============================================ */}
          {/* 💳 PLANOS Y PAGOS (PaymentProvider global aquí también) */}
          {/* ============================================ */}
          <Route path="/planos" element={<PaymentProvider><PlanosPage /></PaymentProvider>} />
          
          {/* 🔥 V15: RUTAS DE CHECKOUT Y POST-PAGO */}
          {/* /checkout redirige a /planos (UpgradeModal usaba esta ruta) */}
          <Route path="/checkout" element={<Navigate to="/planos" replace />} />
          
          {/* Páginas post-pago */}
          <Route path="/payment/success" element={<PaymentProvider><SuccessPage /></PaymentProvider>} />
          <Route path="/payment/pending" element={<PaymentProvider><PendingPage /></PaymentProvider>} />
          <Route path="/payment/cancel" element={<PaymentProvider><CancelPage /></PaymentProvider>} />

          {/* ============================================ */}
          {/* 🏠 VITRINA PÚBLICA (V20 - ULTRA RÁPIDA CON LAZY) */}
          {/* ============================================ */}
          <Route path="/imovel/:id" element={<PublicProperty />} />
          {/* 🔥 V20: Lazy loading + Skeleton inmediato → carga en ~0ms */}
          <Route path="/imoveis" element={
            <Suspense fallback={<VitrineSkeleton />}>
              <PublicPropertiesList />
            </Suspense>
          } />

          {/* ============================================ */}
          {/* 🆕 HELP CENTER - PÚBLICO (ACESSÍVEL SEM LOGIN) */}
          {/* ============================================ */}
          <Route path="/ajuda/documentacao" element={<Documentacao />} />
          <Route path="/ajuda/tutorial" element={<Tutorial />} />
          <Route path="/ajuda/suporte" element={<Suporte />} />
          <Route path="/ajuda/changelog" element={<Changelog />} />

          {/* ============================================ */}
          {/* 🔒 ROTAS PRIVADAS — PaymentProvider + PlanGuard */}
          {/* ============================================ */}
          <Route
            element={
              <PrivateRoute>
                <ProtectedLayout />
              </PrivateRoute>
            }
          >
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<SuspenseWrapper><Dashboard /></SuspenseWrapper>} />

            {/* Clientes */}
            <Route path="/clientes" element={<SuspenseWrapper><Clients /></SuspenseWrapper>} />
            <Route path="/clientes/:id" element={<ClientDetails />} />
            <Route path="/clientes/editar/:id" element={<ClientEdit />} />

            {/* Pipeline + Equipe multi-user */}
            <Route path="/pipeline" element={<SuspenseWrapper><Pipeline /></SuspenseWrapper>} />
            <Route path="/equipe" element={<SuspenseWrapper><Equipe /></SuspenseWrapper>} />

            {/* Imóveis */}
            <Route path="/admin/imoveis" element={<SuspenseWrapper><Properties /></SuspenseWrapper>} />
            <Route path="/admin/imoveis/novo" element={<PropertyForm />} />
            <Route path="/admin/imoveis/editar/:id" element={<PropertyForm />} />
            <Route path="/admin/imoveis/:id" element={<PropertyDetails />} />

            {/* Tarefas */}
            <Route path="/tarefas" element={<SuspenseWrapper><Tasks /></SuspenseWrapper>} />
            <Route path="/tarefas/nova" element={<TaskForm />} />
            <Route path="/tarefas/editar/:id" element={<TaskForm />} />
            <Route path="/tarefas/:id" element={<TaskDetails />} />

            {/* Relatórios */}
            <Route path="/relatorios" element={<SuspenseWrapper><Reports /></SuspenseWrapper>} />

            {/* ============================================ */}
            {/* 📨 MENSAJES — V19 FINAL */}
            {/* ============================================ */}

            {/* ✅ V19 FINAL: Feature Mensagens V2 WhatsApp-Grade (ACTIVO) */}
            <Route path="/mensagens/*" element={<SuspenseWrapper><MensagensRoutes /></SuspenseWrapper>} />

            {/* 🔄 Redirect de /conversas → /mensagens (bookmarks antiguos) */}
            <Route path="/conversas/*" element={<Navigate to="/mensagens" replace />} />

            {/* 🔙 Fallback viejo 48h para rollback de emergencia */}
            <Route path="/mensagens-legacy" element={<SuspenseWrapper><Mensagens /></SuspenseWrapper>} />
            <Route path="/mensagens-legacy/:id" element={<MessageDetails />} />

            {/* Documentos */}
            <Route path="/documentos" element={<SuspenseWrapper><Documentos /></SuspenseWrapper>} />

            {/* Financeiro */}
            <Route path="/financeiro" element={<SuspenseWrapper><Financeiro /></SuspenseWrapper>} />

            {/* Calendário */}
            <Route path="/calendario" element={<SuspenseWrapper><Calendario /></SuspenseWrapper>} />

            {/* Settings y Perfil */}
            <Route path="/settings" element={<SuspenseWrapper><Settings /></SuspenseWrapper>} />
            <Route path="/settings/perfil" element={<SuspenseWrapper><Profile /></SuspenseWrapper>} />

            {/* 🆕 HELP CENTER - TAMBÉM ACESSÍVEL DENTRO DO APP */}
            <Route path="/ajuda/documentacao" element={<Documentacao />} />
            <Route path="/ajuda/tutorial" element={<Tutorial />} />
            <Route path="/ajuda/suporte" element={<Suporte />} />
            <Route path="/ajuda/changelog" element={<Changelog />} />
          </Route>

          {/* FALLBACK */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </NotificationsProvider>
  );
}

// ✅ APP FINAL - ORDEN CORRETA DOS PROVIDERS (V20)
// ThemeProvider está en main.jsx como wrapper raíz único
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppWithProviders />
      </AuthProvider>
    </ErrorBoundary>
  );
}